import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { JOB_SEO } from "@/data/jobSeoData";
import {
    ArrowRight,
    CheckCircle2,
    Star,
    Sparkles,
    Wallet,
    MapPin,
    Briefcase,
    Plus,
    Minus,
} from "lucide-react";

/**
 * SEO-optimised landing page for a single job category.
 * URL: /metiers/:slug (12 jobs, one per family)
 *
 * Populates <title>, meta description, canonical, JSON-LD (JobPosting + FAQPage).
 * Content is unique per job to avoid Google duplicate-content penalty.
 */
export default function JobLandingPage() {
    const { slug } = useParams();
    const data = JOB_SEO[slug];

    useEffect(() => {
        if (!data) return;
        document.title = data.title;
        const setMeta = (name, content, attr = "name") => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        setMeta("description", data.meta_desc);
        if (data.keywords && data.keywords.length) {
            setMeta("keywords", data.keywords.join(", "));
        }
        setMeta("og:title", data.title, "property");
        setMeta("og:description", data.meta_desc, "property");
        setMeta("og:image", data.hero_img, "property");
        setMeta("og:type", "website", "property");
        setMeta("og:url", `https://renforteo.fr/metiers/${data.slug}`, "property");

        let canon = document.querySelector("link[rel=canonical]");
        if (!canon) {
            canon = document.createElement("link");
            canon.setAttribute("rel", "canonical");
            document.head.appendChild(canon);
        }
        canon.setAttribute("href", `https://renforteo.fr/metiers/${data.slug}`);

        // JSON-LD graph : JobPosting + FAQPage + BreadcrumbList
        const ldId = "renforteo-job-jsonld";
        let ld = document.getElementById(ldId);
        if (!ld) {
            ld = document.createElement("script");
            ld.id = ldId;
            ld.type = "application/ld+json";
            document.head.appendChild(ld);
        }
        const minVal = data.salary_details?.min || parseFloat((data.salary_range.match(/\d+/) || ["16"])[0]);
        const maxVal = data.salary_details?.max || parseFloat((data.salary_range.match(/\d+/g) || ["22"]).slice(-1)[0]);
        ld.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "JobPosting",
                    title: data.h1,
                    description: (data.intro_paragraphs || []).join(" "),
                    datePosted: new Date().toISOString().split("T")[0],
                    employmentType: ["CONTRACTOR", "PART_TIME", "TEMPORARY"],
                    hiringOrganization: {
                        "@type": "Organization",
                        name: "Renforteo",
                        sameAs: "https://renforteo.fr",
                    },
                    industry: data.family || "Hospitality & Events",
                    jobLocation: (data.popular_cities || []).map((city) => ({
                        "@type": "Place",
                        address: {
                            "@type": "PostalAddress",
                            addressLocality: city,
                            addressRegion: "Auvergne-Rhône-Alpes",
                            addressCountry: "FR",
                        },
                    })),
                    baseSalary: {
                        "@type": "MonetaryAmount",
                        currency: "EUR",
                        value: {
                            "@type": "QuantitativeValue",
                            minValue: minVal,
                            maxValue: maxVal,
                            unitText: "HOUR",
                        },
                    },
                    directApply: true,
                },
                {
                    "@type": "FAQPage",
                    mainEntity: (data.faqs || []).map((f) => ({
                        "@type": "Question",
                        name: f.q,
                        acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                },
                {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://renforteo.fr" },
                        { "@type": "ListItem", position: 2, name: "Métiers", item: "https://renforteo.fr/metiers" },
                        { "@type": "ListItem", position: 3, name: data.h1, item: `https://renforteo.fr/metiers/${data.slug}` },
                    ],
                },
            ],
        });

        return () => {
            document.title = "Renforteo — Personnel qualifié, devis instantané";
        };
    }, [data]);

    if (!data) {
        return <Navigate to="/" replace />;
    }

    return (
        <div data-testid={`job-page-${slug}`}>
            <Header />

            {/* HERO */}
            <section
                className="relative overflow-hidden"
                style={{
                    background: `linear-gradient(120deg, #0B192C 0%, #0B192C 55%, transparent 100%), url(${data.hero_img}) center/cover no-repeat`,
                }}
                data-testid={`job-hero-${slug}`}
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-2 gap-10 items-center">
                    <div className="text-white">
                        <div className="text-xs text-white/70 mb-5">
                            <Link to="/" className="hover:text-[#F5A623]">Accueil</Link>
                            <span className="mx-2">/</span>
                            <Link to="/metiers" className="hover:text-[#F5A623]">Métiers</Link>
                            <span className="mx-2">/</span>
                            <span className="text-white">{data.slug.replace(/-/g, " ")}</span>
                        </div>
                        <div className="overline text-[#F5A623] mb-3">
                            {data.emoji} {data.family || "Métier"} · {data.slug.replace(/-/g, " ")}
                        </div>
                        <h1
                            className="font-display text-3xl md:text-5xl leading-tight tracking-tight mb-5"
                            data-testid={`job-h1-${slug}`}
                        >
                            {data.h1}
                        </h1>
                        {data.intro_paragraphs && data.intro_paragraphs[0] && (
                            <p className="text-white/80 text-base md:text-lg max-w-xl leading-relaxed">
                                {data.intro_paragraphs[0]}
                            </p>
                        )}
                        <div className="flex flex-col sm:flex-row gap-3 mt-8">
                            <Link
                                to={`/devis?cat=${data.category_key}`}
                                className="btn-gold text-center"
                                data-testid={`job-cta-devis-${slug}`}
                            >
                                Je suis client — obtenir un devis
                                <ArrowRight className="inline ml-1 -mt-1" size={16} />
                            </Link>
                            <Link
                                to={`/candidat/inscription?cat=${data.category_key}`}
                                className="btn-ghost-navy text-center bg-white/10 text-white border-white/30 hover:bg-white hover:text-[#0B192C]"
                                data-testid={`job-cta-candidat-${slug}`}
                            >
                                Je veux travailler
                            </Link>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-2 text-xs text-white/80">
                            <span className="pill pill-gold">💰 {data.salary_range}</span>
                            <span className="pill" style={{ background: "rgba(255,255,255,0.1)", color: "white" }}>
                                Statut indépendant · SIRET requis
                            </span>
                            <span className="pill" style={{ background: "rgba(255,255,255,0.1)", color: "white" }}>
                                📍 Lyon · Grenoble · Valence
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* CONTEXT (multi-paragraph unique content) */}
            {data.intro_paragraphs && data.intro_paragraphs.length > 1 && (
                <section
                    className="max-w-4xl mx-auto px-6 md:px-10 py-16"
                    data-testid={`job-context-${slug}`}
                >
                    <div className="overline mb-3 text-[#F5A623]">Le métier en détail</div>
                    <h2 className="font-display text-2xl md:text-3xl text-[#0B192C] mb-6">
                        {data.family} — {(data.h1 || "").split(" — ")[0] || "métier"} en Auvergne-Rhône-Alpes
                    </h2>
                    {data.intro_paragraphs.slice(1).map((p, i) => (
                        <p key={i} className="text-slate-600 leading-relaxed mb-4">
                            {p}
                        </p>
                    ))}
                </section>
            )}

            {/* CLIENT vs CANDIDATE pitch */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid md:grid-cols-2 gap-6">
                <div className="sd-card p-8">
                    <div className="overline text-[#F5A623] mb-2">Vous êtes une entreprise</div>
                    <h2 className="font-display text-2xl text-[#0B192C] mb-3">
                        {(data.h1 || "").split(" — ")[0] || "Ce métier"} en&nbsp;24&nbsp;h
                    </h2>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                        {data.client_pitch}
                    </p>
                    <Link
                        to={`/devis?cat=${data.category_key}`}
                        className="btn-gold text-sm"
                        data-testid={`job-cta-client-${slug}`}
                    >
                        Obtenir mon devis
                    </Link>
                </div>
                <div className="sd-card p-8">
                    <div className="overline text-[#F5A623] mb-2">Vous cherchez du travail</div>
                    <h2 className="font-display text-2xl text-[#0B192C] mb-3">
                        Devenez {(data.h1 || "").split(" — ")[0]?.toLowerCase() || "prestataire"} indépendant
                    </h2>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                        {data.candidate_pitch}
                    </p>
                    <Link
                        to={`/candidat/inscription?cat=${data.category_key}`}
                        className="btn-ghost-navy text-sm"
                        data-testid={`job-cta-pro-${slug}`}
                    >
                        Créer mon profil pro
                    </Link>
                </div>
            </section>

            {/* SALARY DETAILS */}
            {data.salary_details && (
                <section
                    className="bg-slate-50 border-y border-slate-200"
                    data-testid={`job-salary-${slug}`}
                >
                    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
                        <div className="grid md:grid-cols-[1fr_2fr] gap-10 items-start">
                            <div>
                                <div className="w-12 h-12 rounded-xl bg-[#F5A623] text-[#0B192C] flex items-center justify-center mb-4">
                                    <Wallet size={22} />
                                </div>
                                <div className="overline mb-3">Tarif horaire HT 2026</div>
                                <h2 className="font-display text-3xl md:text-4xl text-[#0B192C] mb-3">
                                    {data.salary_range}
                                </h2>
                                <p className="text-slate-500 text-sm leading-relaxed">
                                    Honoraires HT facturés au client par le professionnel indépendant.
                                    Vous conservez ce montant après frais plateforme (8 %) et vos
                                    cotisations micro-entrepreneur (~21,2 % du CA).
                                </p>
                            </div>
                            <div>
                                <div className="grid gap-3">
                                    {data.salary_details.factors.map((f, i) => (
                                        <div
                                            key={i}
                                            className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 p-4"
                                        >
                                            <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                                            <span className="text-sm text-slate-700">{f}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* TYPICAL MISSIONS + PREREQUISITES */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid md:grid-cols-2 gap-12">
                <div>
                    <div className="overline mb-3 text-[#F5A623]">Missions types</div>
                    <h2 className="font-display text-2xl text-[#0B192C] mb-4">Ce que vous ferez</h2>
                    <ul className="space-y-3">
                        {data.typical_missions.map((m, i) => (
                            <li key={i} className="flex gap-3 text-sm text-slate-700">
                                <Sparkles size={16} className="text-[#F5A623] flex-shrink-0 mt-0.5" />
                                <span>{m}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <div className="overline mb-3 text-[#F5A623]">Pré-requis</div>
                    <h2 className="font-display text-2xl text-[#0B192C] mb-4">Ce qu'il vous faut</h2>
                    <ul className="space-y-3">
                        {data.prerequisites.map((p, i) => (
                            <li key={i} className="flex gap-3 text-sm text-slate-700">
                                <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* JOB CONTEXT (sector info) */}
            {data.job_context && (
                <section
                    className="bg-[#0B192C] text-white"
                    data-testid={`job-market-${slug}`}
                >
                    <div className="max-w-4xl mx-auto px-6 md:px-10 py-16">
                        <div className="overline mb-3 text-[#F5A623]">Marché & contexte AURA</div>
                        <h2 className="font-display text-2xl md:text-3xl mb-5">
                            Pourquoi c'est un métier porteur en Auvergne-Rhône-Alpes
                        </h2>
                        <p className="text-white/80 leading-relaxed text-base md:text-lg">
                            {data.job_context}
                        </p>
                    </div>
                </section>
            )}

            {/* PROS & CONS */}
            {data.pros_cons && (
                <section
                    className="max-w-6xl mx-auto px-6 md:px-10 py-16"
                    data-testid={`job-pros-cons-${slug}`}
                >
                    <div className="text-center mb-10">
                        <div className="overline mb-3 text-[#F5A623]">Avantages & contraintes</div>
                        <h2 className="font-display text-2xl md:text-3xl text-[#0B192C]">
                            Ce métier vous correspond-il ?
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                        <div className="sd-card p-6 border-l-4 border-emerald-500">
                            <div className="flex items-center gap-2 mb-4">
                                <Plus size={18} className="text-emerald-600" />
                                <h3 className="font-display text-lg text-[#0B192C]">Points forts</h3>
                            </div>
                            <ul className="space-y-2.5 text-sm text-slate-700">
                                {data.pros_cons.pros.map((p, i) => (
                                    <li key={i} className="flex gap-2">
                                        <span className="text-emerald-600">✓</span>
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="sd-card p-6 border-l-4 border-amber-500">
                            <div className="flex items-center gap-2 mb-4">
                                <Minus size={18} className="text-amber-600" />
                                <h3 className="font-display text-lg text-[#0B192C]">À prendre en compte</h3>
                            </div>
                            <ul className="space-y-2.5 text-sm text-slate-700">
                                {data.pros_cons.cons.map((c, i) => (
                                    <li key={i} className="flex gap-2">
                                        <span className="text-amber-600">•</span>
                                        <span>{c}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            )}

            {/* CITIES SEO BLOCK */}
            <section
                className="bg-slate-50 border-y border-slate-200"
                data-testid={`job-cities-${slug}`}
            >
                <div className="max-w-6xl mx-auto px-6 md:px-10 py-14">
                    <div className="overline mb-3 text-[#F5A623]">Zone d'intervention</div>
                    <h2 className="font-display text-2xl md:text-3xl text-[#0B192C] mb-3">
                        {data.slug.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase())} disponible en Auvergne-Rhône-Alpes
                    </h2>
                    <p className="text-slate-500 text-sm mb-6 max-w-2xl">
                        Renforteo se concentre sur les trois métropoles principales de la région
                        AURA et leur périphérie immédiate — vivier de professionnels vérifié et
                        opérationnel.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {data.popular_cities.map((c) => (
                            <span
                                key={c}
                                className="text-xs font-semibold text-[#0B192C] bg-white border border-slate-200 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
                            >
                                <MapPin size={11} className="text-[#F5A623]" />
                                {c}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                className="max-w-4xl mx-auto px-6 md:px-10 py-16"
                data-testid={`job-faq-${slug}`}
            >
                <div className="text-center mb-10">
                    <div className="overline mb-3 text-[#F5A623]">FAQ</div>
                    <h2 className="font-display text-2xl md:text-3xl text-[#0B192C]">
                        Questions fréquentes
                    </h2>
                </div>
                <div className="space-y-4">
                    {data.faqs.map((f, i) => (
                        <details
                            key={i}
                            className="sd-card p-5 group"
                            data-testid={`job-faq-${slug}-${i}`}
                        >
                            <summary className="font-semibold text-[#0B192C] cursor-pointer flex justify-between items-center list-none">
                                <span>{f.q}</span>
                                <span className="text-[#F5A623] group-open:rotate-45 transition-transform text-xl">
                                    +
                                </span>
                            </summary>
                            <p className="text-sm text-slate-600 mt-3 leading-relaxed">{f.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            {/* RELATED JOBS (cross-linking SEO) */}
            {data.related_slugs && data.related_slugs.length > 0 && (
                <section
                    className="bg-slate-50 border-t border-slate-200"
                    data-testid={`job-related-${slug}`}
                >
                    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
                        <div className="text-center mb-10">
                            <div className="overline mb-3 text-[#F5A623]">
                                Métiers connexes
                            </div>
                            <h2 className="font-display text-2xl md:text-3xl text-[#0B192C]">
                                Vous pourriez aussi consulter
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-4">
                            {data.related_slugs.map((rs) => {
                                const r = JOB_SEO[rs];
                                if (!r) return null;
                                return (
                                    <Link
                                        key={rs}
                                        to={`/metiers/${rs}`}
                                        className="group sd-card p-5 hover:border-[#F5A623] transition-colors"
                                        data-testid={`job-related-link-${rs}`}
                                    >
                                        <div className="text-3xl mb-2">{r.emoji}</div>
                                        <div className="font-display text-base text-[#0B192C] mb-1 group-hover:text-[#F5A623] transition-colors">
                                            {r.slug.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase())}
                                        </div>
                                        <div className="text-xs text-slate-500 line-clamp-2">
                                            {r.client_pitch}
                                        </div>
                                        <div className="text-xs text-[#F5A623] font-semibold mt-3 inline-flex items-center gap-1">
                                            Découvrir <ArrowRight size={12} />
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* FINAL CTA */}
            <section className="max-w-4xl mx-auto px-6 md:px-10 py-20 text-center">
                <Star className="mx-auto text-[#F5A623] mb-4" size={32} />
                <h2 className="font-display text-3xl md:text-4xl text-[#0B192C] mb-4">
                    Prêt à démarrer ?
                </h2>
                <p className="text-slate-500 max-w-xl mx-auto mb-8">
                    Inscription en 2 minutes. Vérification des documents en 24 h. Missions
                    disponibles immédiatement.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link
                        to={`/devis?cat=${data.category_key}`}
                        className="btn-gold"
                        data-testid={`job-cta-final-devis-${slug}`}
                    >
                        <Briefcase size={16} className="inline mr-2 -mt-1" />
                        Obtenir un devis
                    </Link>
                    <Link
                        to={`/candidat/inscription?cat=${data.category_key}`}
                        className="btn-ghost-navy"
                        data-testid={`job-cta-final-candidat-${slug}`}
                    >
                        Devenir {data.slug.replace(/-/g, " ")}
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
}
